import React from "react";
import { Link } from "react-router-dom";
import { Table } from "reactstrap";
import { Button, Icon } from "shared";
import { SiteComparisonTableProps } from "./type";
import numeral from "numeral";
import _ from "lodash";
import t from "utils/Lang";

const uiLayoutDefinition = [
  { type: "break", name: t().site_detail__tab_summary },
  { type: "text", attributeName: "type" },
  { type: "number", attributeName: "area" },
  { type: "number", attributeName: "seat_count" },
  { type: "text", attributeName: "store_format" },
  { type: "text", attributeName: "store_type" },
  { type: "text", attributeName: "accessibility" },
  { type: "text", attributeName: "visibility" },
  { type: "break", name: t().site_detail__tab_rental_information },
  { type: "number", attributeName: "lease_rate" },
  { type: "number", attributeName: "lease_term_year" },
  { type: "number", attributeName: "increase_rate_year_period" },
  { type: "text", attributeName: "increase_rate_period_note" },
  { type: "number", attributeName: "free_construction_days" },
  { type: "break", name: "P&L" },
  { type: "number", attributeName: "total_population" },
  { type: "number", attributeName: "conversion_rate" },
  { type: "number", attributeName: "revenue", showTimestamp: true },
  { type: "number", attributeName: "revenue_site_dev" },
  { type: "number", attributeName: "ebitda", showTimestamp: true },
  { type: "number", attributeName: "ebitda_site_dev" },
  { type: "break", name: t().site_summary_tab__investment_cost_and_break_even },
  { type: "number", attributeName: "initial_investment" },
  { type: "number", attributeName: "payback_period" },
  { type: "number", attributeName: "roi" },
];

const SiteComparisonTable = ({
  comparisonMetrics = [],
  siteId,
  viewOnly,
  siteIdsToCompare = [],
  storeIdsToCompare = [],
  removeElementFromComparison,
  toggleSelectionModal,
  toggleRentalCalculationModal,
  metricLabels,
}: SiteComparisonTableProps) => {
  return (
    <Table>
      <thead>
        <tr>
          <th style={styles.headingCell}>Metrics</th>
          {comparisonMetrics?.map((element: any) => {
            let externalUrl = "";
            if (element.type === "site") {
              externalUrl = `/sitev2/${element.id}`;
            } else if (element.type === "store") {
              externalUrl = `/store/${element.id}`;
            } else if (element.type === "location") {
              externalUrl = `/market_information/${element.id}`;
            }
            return (
              <th
                key={`${element.type}-${element.id}`}
                style={styles.headingCell}
                className={`site-comparison__comparison-table__metrics ${
                  element.id == siteId && element.type === "site"
                    ? "site-comparison__comparison-table-cell__current-site"
                    : ""
                }`}
              >
                <div className="site-comparison__comparison-table__label">
                  <Link target="_blank" to={externalUrl}>
                    {element?.name}
                  </Link>
                  {!(element.type == "site" && element.id == siteId) &&
                    !viewOnly && (
                      <>
                        <div style={{ width: 5 }} />
                        <Icon
                          name="close"
                          fill
                          size="small"
                          onClick={removeElementFromComparison(element)}
                        />
                      </>
                    )}
                </div>
              </th>
            );
          })}
          {_.uniq(siteIdsToCompare).length + _.uniq(storeIdsToCompare).length <
            4 &&
            !viewOnly && (
              <th className="site-comparison__comparison-table__add-element-th">
                <Button
                  type="primary"
                  label="Thêm"
                  onClick={toggleSelectionModal}
                  size="small"
                  icon="add"
                />
              </th>
            )}
        </tr>
      </thead>
      <tbody>
        {uiLayoutDefinition.map((item: any, index: any) => {
          if (item.type === "break") {
            return (
              <tr
                key={`row-${index}`}
                className="site-comparison__comparison-table__section-header"
              >
                <td colSpan={comparisonMetrics.length + 1}>{item.name}</td>
              </tr>
            );
          }
          const metricName =
            metricLabels[item.attributeName] || item.attributeName;
          const thMetricCell = (
            <td
              className="site-comparison__comparison-table__metrics-name-wrapper"
              scope="row"
            >
              {metricName}
            </td>
          );
          const tdElement = comparisonMetrics?.map((element: any) => (
            <td
              key={`${element.type}-${element.id}`}
              className={`site-comparison__comparison-table__metrics ${
                element.id == siteId && element.type === "site"
                  ? "site-comparison__comparison-table-cell__current-site"
                  : ""
              }`}
            >
              {item.type === "number" ? (
                <span>
                  {item.attributeName === "lease_rate" &&
                  element.rental_calculation_method ===
                    "revenue_share" ? null : (
                    <span>
                      {numeral(element[item.attributeName]).format("0,0")}
                    </span>
                  )}
                  {item.attributeName === "lease_rate" &&
                    element.rental_calculation_method && (
                      <a
                        style={styles.rentalCalculationMethod}
                        onClick={toggleRentalCalculationModal(
                          element.rental_calculation_method,
                          element.id
                        )}
                      >
                        (Cách tính: {element.rental_calculation_method_name})
                      </a>
                    )}
                </span>
              ) : (
                <span>
                  {element[item.attributeName]}
                  {item.customComponent}
                </span>
              )}
              {item.showTimestamp &&
                element[`${item.attributeName}_timestamp`] && (
                  <span style={styles.timestamp}>
                    {`(${element[`${item.attributeName}_timestamp`]})`}
                  </span>
                )}
            </td>
          ));
          return (
            <tr key={`row-${index}`}>
              {thMetricCell}
              {tdElement}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const styles: { [name: string]: React.CSSProperties } = {
  headingCell: {
    verticalAlign: "middle",
  },
  timestamp: {
    marginLeft: 6,
    fontSize: 12,
    color: "var(--color-muted)",
  },
  rentalCalculationMethod: {
    marginLeft: 6,
    color: "var(--primary)",
  },
};

export default SiteComparisonTable;
